import * as React from 'react';
import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    Label,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import { apiFetch } from '../../../lib/api-client';
import ProfilerIcon from '../../../Profiler.ico';
import {
    AgentTaskExtraContextItem,
    AgentTaskEnqueueSubmissionMode,
    AgentTaskListDetailResult,
    AgentTaskProfileStreamResult,
    AgentTaskProposedChangeResult,
    AgentTaskProposedChangeResultSet,
    AgentTaskProposalVote,
    AgentTaskProposalVoteUpdateResult,
    AgentTaskTaskDetailResult,
    EnqueueAgentTaskPromptPreviewResult,
    EnqueueAgentTasksResult,
    JOB_RUNTIME_SUBSTITUTION_OPTIONS,
    JobDefinitionCollectionResult,
    JobDefinitionSummary,
    PromptRuntimeSubstitutionRange,
    CallGraphDownloadFormat,
    TASK_CALL_GRAPH_DOWNLOAD_LABELS,
    TASK_PROFILE_STREAM_KEYS,
    TaskProfileStreamKind,
    SyntheticBanner,
    StatCard,
    buildAzureProfilerViewerUrl,
    buildTaskDocumentTitle,
    buildTaskCallGraphDownloadUrl,
    formatCompactDate,
    formatNumber,
    getDownloadErrorMessage,
    getDownloadFileName,
    getJobStatusLabel,
    getTaskLifecycleStatus,
    getTaskLifecycleStatusLabel,
    getTaskListDisplayName,
    getAgentTaskProposedChanges,
    insertSubstitutionReference,
    triggerBrowserDownload,
    updateAgentTaskProposedChangeVote,
    useCurrentService,
    useDocumentTitle,
} from '../shared';
import { AgentTaskFlameGraph } from './AgentTaskFlameGraph';
import { TaskClarificationsTab } from './TaskClarificationsTab';
import {
    formatExecutionDate,
    TaskExecutionLinks,
    TaskJobExecutions,
} from './TaskJobExecutions';
import { PullRequestSummaryList } from './PullRequestSummaryList';
import {
    getCurrentActiveProposals,
    getOkAllProposalVote,
    TaskScopedProposalReview,
} from '../proposedChanges/TaskScopedProposalReview';
import {
    calculateTaskImpact,
    getFirstRelevantTaskTab,
    selectLatestWorkItem,
    TaskDetailTabKey,
} from './taskDetailPresentation';

const PROMPT_PLACEHOLDER_REGEX = /\{\{\s*([^{}]+?)\s*\}\}/g;

function updatePromptRanges(
    previousText: string,
    nextText: string,
    ranges: PromptRuntimeSubstitutionRange[],
) {
    let commonPrefixLength = 0;
    while (commonPrefixLength < previousText.length
        && commonPrefixLength < nextText.length
        && previousText[commonPrefixLength] === nextText[commonPrefixLength]) {
        commonPrefixLength++;
    }

    let commonSuffixLength = 0;
    while (commonSuffixLength < previousText.length - commonPrefixLength
        && commonSuffixLength < nextText.length - commonPrefixLength
        && previousText[previousText.length - commonSuffixLength - 1]
            === nextText[nextText.length - commonSuffixLength - 1]) {
        commonSuffixLength++;
    }

    const previousChangeEnd = previousText.length - commonSuffixLength;
    const shift = nextText.length - previousText.length;
    return ranges.flatMap(range => {
        const rangeEnd = range.start + range.length;
        if (rangeEnd <= commonPrefixLength) {
            return [range];
        }

        if (range.start >= previousChangeEnd) {
            return [{
                ...range,
                start: range.start + shift,
            }];
        }

        return [];
    });
}

function rangesOverlap(
    left: PromptRuntimeSubstitutionRange,
    right: PromptRuntimeSubstitutionRange,
) {
    return left.start < right.start + right.length
        && right.start < left.start + left.length;
}

function getPromptPlaceholderRanges(text: string) {
    return Array.from(text.matchAll(PROMPT_PLACEHOLDER_REGEX), match => ({
        start: match.index,
        length: match[0].length,
    }));
}

export function TaskDetailContent(props: {
    serviceName: string;
    taskListId: number;
    taskListDisplayName?: string;
    taskId: number;
    snapshotId?: number;
    standalone?: boolean;
    onProposalCountsChanged?: (
        taskId: number,
        counts: Pick<
            AgentTaskProposalVoteUpdateResult,
            'activeProposalCount' | 'votedProposalCount' | 'okProposalCount'
        >,
    ) => void;
    hasPreviousTask?: boolean;
    hasNextTask?: boolean;
    onNavigatePreviousTask?: () => void;
    onNavigateNextTask?: () => void;
    prefetchedTask?: AgentTaskTaskDetailResult;
}) {
    const {
        serviceName,
        taskListId,
        taskListDisplayName,
        taskId,
        snapshotId,
        standalone,
        onProposalCountsChanged,
        hasPreviousTask,
        hasNextTask,
        onNavigatePreviousTask,
        onNavigateNextTask,
        prefetchedTask,
    } = props;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const service = useCurrentService(serviceName);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [task, setTask] = React.useState<AgentTaskTaskDetailResult | null>(null);
    const [loadedTaskListDisplayName, setLoadedTaskListDisplayName] = React.useState<string | undefined>(taskListDisplayName);
    const [profileStreamLoadingKind, setProfileStreamLoadingKind] = React.useState<TaskProfileStreamKind | null>(null);
    const [profileStreamError, setProfileStreamError] = React.useState<string | null>(null);
    const [callGraphDownloadLoadingFormat, setCallGraphDownloadLoadingFormat] = React.useState<CallGraphDownloadFormat | null>(null);
    const [callGraphDownloadError, setCallGraphDownloadError] = React.useState<string | null>(null);
    const [extraContextError, setExtraContextError] = React.useState<string | null>(null);
    const [newExtraContextInfo, setNewExtraContextInfo] = React.useState('');
    const [editingContextId, setEditingContextId] = React.useState<number | null>(null);
    const [editingContextInfo, setEditingContextInfo] = React.useState('');
    const [editingContextDisabled, setEditingContextDisabled] = React.useState(false);
    const [savingContextId, setSavingContextId] = React.useState<number | 'create' | null>(null);
    const [availableJobs, setAvailableJobs] = React.useState<JobDefinitionSummary[]>([]);
    const [selectedJobName, setSelectedJobName] = React.useState<string | undefined>();
    const [jobsLoading, setJobsLoading] = React.useState(false);
    const [enqueueLoading, setEnqueueLoading] = React.useState(false);
    const [enqueueError, setEnqueueError] = React.useState<string | null>(null);
    const [enqueueMessage, setEnqueueMessage] = React.useState<string | null>(null);
    const [selectedProposalIds, setSelectedProposalIds] = React.useState<Set<number>>(() => new Set());
    const [proposalResult, setProposalResult] = React.useState<AgentTaskProposedChangeResultSet | undefined>();
    const [proposalsLoading, setProposalsLoading] = React.useState(true);
    const [proposalsError, setProposalsError] = React.useState<string | null>(null);
    const [updatingProposalVoteIds, setUpdatingProposalVoteIds] = React.useState<Set<number>>(() => new Set());
    const [okAllLoading, setOkAllLoading] = React.useState(false);
    const [freeFormPreviewLoading, setFreeFormPreviewLoading] = React.useState(false);
    const [freeFormSubmitting, setFreeFormSubmitting] = React.useState(false);
    const [freeFormDialogOpen, setFreeFormDialogOpen] = React.useState(false);
    const [freeFormPromptText, setFreeFormPromptText] = React.useState('');
    const [freeFormAllowedTokens, setFreeFormAllowedTokens] = React.useState<string[]>([]);
    const [freeFormSelectedToken, setFreeFormSelectedToken] = React.useState<string | undefined>();
    const [freeFormRuntimeSubstitutions, setFreeFormRuntimeSubstitutions] =
        React.useState<PromptRuntimeSubstitutionRange[]>([]);
    const [freeFormProtectedPlaceholders, setFreeFormProtectedPlaceholders] =
        React.useState<PromptRuntimeSubstitutionRange[]>([]);
    const [freeFormError, setFreeFormError] = React.useState<string | null>(null);
    const [freeFormContainsSasPlaceholders, setFreeFormContainsSasPlaceholders] = React.useState(false);
    const [freeFormProposalIds, setFreeFormProposalIds] = React.useState<number[]>([]);
    const freeFormEditorRef = React.useRef<HTMLTextAreaElement | null>(null);
    const proposalRequestId = React.useRef(0);

    React.useEffect(() => {
        if (prefetchedTask?.taskId === taskId) {
            setError(null);
            setTask(prefetchedTask);
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);
        setError(null);

        const query = snapshotId !== undefined ? `?snapshotId=${snapshotId}` : '';
        ApiHelper(`/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}${query}`)
            .then((response: AgentTaskTaskDetailResult) => {
                if (!isMounted) {
                    return;
                }

                setTask(response);
                setLoading(false);
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setError(err.message || 'Failed to load task details.');
                setTask(null);
                setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [prefetchedTask, serviceName, snapshotId, taskId, taskListId]);

    React.useEffect(() => {
        if (!task) {
            setAvailableJobs([]);
            setSelectedJobName(undefined);
            return;
        }

        let isMounted = true;
        setJobsLoading(true);
        setEnqueueError(null);

        ApiHelper(`/api/service/${serviceName}/jobdefinitions?taskType=${task.taskType}`)
            .then((response: JobDefinitionCollectionResult) => {
                if (!isMounted) {
                    return;
                }

                const jobs = (response.jobDefinitions ?? [])
                    .slice()
                    .sort((left, right) => left.name.localeCompare(right.name));
                setAvailableJobs(jobs);
                setSelectedJobName(current =>
                    current && jobs.some(job => job.name === current)
                        ? current
                        : jobs[0]?.name);
                setJobsLoading(false);
            })
            .catch((err: Error) => {
                if (!isMounted) {
                    return;
                }

                setAvailableJobs([]);
                setSelectedJobName(undefined);
                setEnqueueError(err.message || 'Failed to load job definitions.');
                setJobsLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [serviceName, task?.taskType]);

    const loadProposals = React.useCallback(async () => {
        const currentRequestId = ++proposalRequestId.current;
        setProposalsLoading(true);
        setProposalsError(null);
        try {
            const result = await getAgentTaskProposedChanges(serviceName, {
                view: 'current',
                pageNumber: 0,
                pageSize: 100,
                sortBy: 'lastAnalyzed',
                sortDirection: 'desc',
                taskListId,
                taskId,
            });
            if (proposalRequestId.current !== currentRequestId) {
                return;
            }
            setProposalResult(result.resultSets[0]);
        } catch (err) {
            if (proposalRequestId.current !== currentRequestId) {
                return;
            }
            setProposalResult(undefined);
            setProposalsError(err instanceof Error ? err.message : 'Failed to load proposed changes.');
        } finally {
            if (proposalRequestId.current === currentRequestId) {
                setProposalsLoading(false);
            }
        }
    }, [serviceName, taskId, taskListId]);

    React.useEffect(() => {
        void loadProposals();
        return () => {
            proposalRequestId.current += 1;
        };
    }, [loadProposals]);

    React.useEffect(() => {
        if (taskListDisplayName) {
            setLoadedTaskListDisplayName(taskListDisplayName);
            return;
        }

        let isMounted = true;
        ApiHelper(`/api/service/${serviceName}/agenttasklists/${taskListId}`)
            .then((response: AgentTaskListDetailResult) => {
                if (!isMounted) {
                    return;
                }

                setLoadedTaskListDisplayName(response.taskList.name);
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setLoadedTaskListDisplayName(undefined);
            });

        return () => {
            isMounted = false;
        };
    }, [serviceName, taskListDisplayName, taskListId]);

    React.useEffect(() => {
        setProfileStreamLoadingKind(null);
        setProfileStreamError(null);
        setCallGraphDownloadLoadingFormat(null);
        setCallGraphDownloadError(null);
        setExtraContextError(null);
        setNewExtraContextInfo('');
        setEditingContextId(null);
        setEditingContextInfo('');
        setEditingContextDisabled(false);
        setSavingContextId(null);
        setEnqueueLoading(false);
        setEnqueueError(null);
        setEnqueueMessage(null);
        setSelectedProposalIds(new Set());
        resetFreeFormEditor();
    }, [serviceName, taskId, taskListId, snapshotId]);

    const effectiveTaskListDisplayName = getTaskListDisplayName(taskListDisplayName ?? loadedTaskListDisplayName, taskListId);
    useDocumentTitle(task ? buildTaskDocumentTitle(effectiveTaskListDisplayName, task.scope) : undefined);

    async function handleOpenProfileStream(kind: TaskProfileStreamKind) {
        if (!task?.snapshot) {
            return;
        }

        setProfileStreamLoadingKind(kind);
        setProfileStreamError(null);

        try {
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/profilestreams`,
                'POST',
                {
                    snapshotId: task.snapshot.snapshotId,
                    kind: TASK_PROFILE_STREAM_KEYS[kind].requestValue,
                },
            ) as AgentTaskProfileStreamResult;

            window.open(buildAzureProfilerViewerUrl(result.sasUrl), '_blank', 'noopener,noreferrer');
        } catch (err) {
            setProfileStreamError(err instanceof Error ? err.message : 'Failed to generate the task profile stream.');
        } finally {
            setProfileStreamLoadingKind(null);
        }
    }

    async function handleDownloadCallGraph(format: CallGraphDownloadFormat) {
        if (!task?.snapshot) {
            return;
        }

        setCallGraphDownloadLoadingFormat(format);
        setCallGraphDownloadError(null);

        try {
            const response = await apiFetch(buildTaskCallGraphDownloadUrl(
                serviceName,
                taskListId,
                taskId,
                task.snapshot.snapshotId,
                format,
            ));

            if (!response.ok) {
                throw new Error(await getDownloadErrorMessage(
                    response,
                    `Failed to download ${TASK_CALL_GRAPH_DOWNLOAD_LABELS[format]}.`,
                ));
            }

            const blob = await response.blob();
            const fileName = getDownloadFileName(
                response.headers.get('Content-Disposition'),
                TASK_CALL_GRAPH_DOWNLOAD_LABELS[format],
            );

            triggerBrowserDownload(blob, fileName);
        } catch (err) {
            setCallGraphDownloadError(err instanceof Error ? err.message : `Failed to download ${TASK_CALL_GRAPH_DOWNLOAD_LABELS[format]}.`);
        } finally {
            setCallGraphDownloadLoadingFormat(null);
        }
    }

    function replaceExtraContext(updatedItem: AgentTaskExtraContextItem) {
        setTask(current => current ? {
            ...current,
            extraContext: current.extraContext.map(item => item.contextId === updatedItem.contextId ? updatedItem : item),
        } : current);
    }

    async function saveExtraContext(contextId: number, info: string, disabled: boolean, onSaved?: () => void) {
        const trimmedInfo = info.trim();
        if (!trimmedInfo) {
            setExtraContextError('Extra context cannot be empty.');
            return;
        }

        setSavingContextId(contextId);
        setExtraContextError(null);

        try {
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/context/${contextId}`,
                'PATCH',
                {
                    info: trimmedInfo,
                    disabled,
                },
            ) as AgentTaskExtraContextItem;

            replaceExtraContext(result);
            onSaved?.();
        } catch (err) {
            setExtraContextError(err instanceof Error ? err.message : 'Failed to update extra context.');
        } finally {
            setSavingContextId(null);
        }
    }

    async function handleCreateExtraContext() {
        const trimmedInfo = newExtraContextInfo.trim();
        if (!trimmedInfo) {
            setExtraContextError('Enter extra context before adding it.');
            return;
        }

        setSavingContextId('create');
        setExtraContextError(null);

        try {
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}/context`,
                'POST',
                { info: trimmedInfo },
            ) as AgentTaskExtraContextItem;

            setTask(current => current ? {
                ...current,
                extraContext: [...current.extraContext, result],
            } : current);
            setNewExtraContextInfo('');
        } catch (err) {
            setExtraContextError(err instanceof Error ? err.message : 'Failed to add extra context.');
        } finally {
            setSavingContextId(null);
        }
    }

    function beginEditingExtraContext(item: AgentTaskExtraContextItem) {
        setEditingContextId(item.contextId);
        setEditingContextInfo(item.info);
        setEditingContextDisabled(item.disabled);
        setExtraContextError(null);
    }

    function cancelEditingExtraContext() {
        setEditingContextId(null);
        setEditingContextInfo('');
        setEditingContextDisabled(false);
    }

    async function handleSaveEditedExtraContext() {
        if (editingContextId === null) {
            return;
        }

        await saveExtraContext(editingContextId, editingContextInfo, editingContextDisabled, () => {
            cancelEditingExtraContext();
        });
    }

    async function handleToggleExtraContextDisabled(item: AgentTaskExtraContextItem) {
        await saveExtraContext(item.contextId, item.info, !item.disabled);
    }

    async function handleEnqueue() {
        if (!task?.snapshot || !selectedJobName) {
            return;
        }

        setEnqueueLoading(true);
        setEnqueueError(null);
        setEnqueueMessage(null);

        try {
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/enqueue`,
                'POST',
                {
                    taskIds: [task.taskId],
                    jobName: selectedJobName,
                    snapshotId: task.snapshot.snapshotId,
                    modelConfiguration: null,
                    submissionMode: AgentTaskEnqueueSubmissionMode.QueueInternally,
                    selectedProposalIds: Array.from(selectedProposalIds),
                },
            ) as EnqueueAgentTasksResult;

            ensureEnqueueSucceeded(result);
            await refreshTaskDetail();
            setEnqueueMessage(`${selectedJobName} was queued internally.`);
        } catch (err) {
            setEnqueueError(err instanceof Error ? err.message : 'Failed to enqueue the task.');
        } finally {
            setEnqueueLoading(false);
        }
    }

    function resetFreeFormEditor() {
        setFreeFormPreviewLoading(false);
        setFreeFormSubmitting(false);
        setFreeFormDialogOpen(false);
        setFreeFormPromptText('');
        setFreeFormAllowedTokens([]);
        setFreeFormSelectedToken(undefined);
        setFreeFormRuntimeSubstitutions([]);
        setFreeFormProtectedPlaceholders([]);
        setFreeFormError(null);
        setFreeFormContainsSasPlaceholders(false);
        setFreeFormProposalIds([]);
    }

    function ensureEnqueueSucceeded(result: EnqueueAgentTasksResult) {
        if (result.workItems.length > 0
            && !result.stoppedOnFailure
            && (result.failures?.length ?? 0) === 0) {
            return;
        }

        throw new Error(
            result.failures?.map(failure => failure.message).filter(Boolean).join('\n')
            || result.failureMessage
            || 'The task could not be enqueued.',
        );
    }

    async function refreshTaskDetail() {
        const query = snapshotId !== undefined ? `?snapshotId=${snapshotId}` : '';
        const refreshedTask = await ApiHelper(
            `/api/service/${serviceName}/agenttasklists/${taskListId}/tasks/${taskId}${query}`,
        ) as AgentTaskTaskDetailResult;
        setTask(refreshedTask);
    }

    async function handleOpenFreeFormEnqueue() {
        if (!task?.snapshot || !selectedJobName) {
            return;
        }

        setFreeFormPreviewLoading(true);
        setEnqueueError(null);
        setEnqueueMessage(null);

        try {
            const proposalIds = Array.from(selectedProposalIds);
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/enqueue:preview`,
                'POST',
                {
                    taskIds: [task.taskId],
                    jobName: selectedJobName,
                    snapshotId: task.snapshot.snapshotId,
                    modelConfiguration: null,
                    selectedProposalIds: proposalIds,
                },
            ) as EnqueueAgentTaskPromptPreviewResult;
            const allowedTokens = result.availableRuntimeSubstitutions ?? [];
            const runtimeSubstitutions = result.editableRuntimeSubstitutions ?? [];
            const protectedPlaceholders = getPromptPlaceholderRanges(result.editablePromptText)
                .filter(placeholder => !runtimeSubstitutions.some(runtime =>
                    runtime.start === placeholder.start && runtime.length === placeholder.length));

            setFreeFormPromptText(result.editablePromptText ?? '');
            setFreeFormAllowedTokens(allowedTokens);
            setFreeFormSelectedToken(allowedTokens[0]);
            setFreeFormRuntimeSubstitutions(runtimeSubstitutions);
            setFreeFormProtectedPlaceholders(protectedPlaceholders);
            setFreeFormContainsSasPlaceholders(result.containsSasPlaceholders);
            setFreeFormProposalIds(proposalIds);
            setFreeFormError(null);
            setFreeFormDialogOpen(true);
        } catch (err) {
            setEnqueueError(err instanceof Error ? err.message : 'Failed to prepare the free-form prompt.');
        } finally {
            setFreeFormPreviewLoading(false);
        }
    }

    async function handleSubmitFreeFormEnqueue() {
        if (!task?.snapshot || !selectedJobName) {
            return;
        }

        setFreeFormSubmitting(true);
        setFreeFormError(null);

        try {
            const result = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${taskListId}/enqueue`,
                'POST',
                {
                    taskIds: [task.taskId],
                    jobName: selectedJobName,
                    snapshotId: task.snapshot.snapshotId,
                    modelConfiguration: null,
                    submissionMode: AgentTaskEnqueueSubmissionMode.QueueInternally,
                    selectedProposalIds: freeFormProposalIds,
                    promptTextOverride: freeFormPromptText,
                    promptRuntimeSubstitutions: freeFormRuntimeSubstitutions
                        .slice()
                        .sort((left, right) => left.start - right.start),
                },
            ) as EnqueueAgentTasksResult;

            ensureEnqueueSucceeded(result);
            await refreshTaskDetail();
            setEnqueueMessage(`${selectedJobName} was queued internally with a free-form prompt.`);
            resetFreeFormEditor();
        } catch (err) {
            setFreeFormError(err instanceof Error ? err.message : 'Failed to enqueue the free-form prompt.');
        } finally {
            setFreeFormSubmitting(false);
        }
    }

    function handleProposalSelection(change: AgentTaskProposedChangeResult, selected: boolean) {
        setSelectedProposalIds(current => {
            const next = new Set(current);
            if (selected) {
                next.add(change.analysisItemId);
            } else {
                next.delete(change.analysisItemId);
            }
            return next;
        });
    }

    function applyProposalUpdate(result: AgentTaskProposalVoteUpdateResult) {
        setProposalResult(current => {
            if (!current) {
                return current;
            }
            const refreshedById = new Map(result.proposals.map(change => [change.analysisItemId, change]));
            const retained = current.proposedChangeEvents
                .filter(change => !change.isCurrent && !refreshedById.has(change.analysisItemId));
            return {
                ...current,
                proposedChangeEvents: [...retained, ...result.proposals],
            };
        });
        const eligibleIds = new Set(result.proposals
            .filter(change =>
                change.isCurrent
                && change.outcome !== 'discarded'
                && change.vote === AgentTaskProposalVote.Ok)
            .map(change => change.analysisItemId));
        setSelectedProposalIds(current =>
            new Set(Array.from(current).filter(analysisItemId => eligibleIds.has(analysisItemId))));
        onProposalCountsChanged?.(taskId, {
            activeProposalCount: result.activeProposalCount,
            votedProposalCount: result.votedProposalCount,
            okProposalCount: result.okProposalCount,
        });
    }

    async function handleProposalVote(
        change: AgentTaskProposedChangeResult,
        vote: AgentTaskProposalVote | null,
    ) {
        if (!change.isCurrent || updatingProposalVoteIds.size > 0 || okAllLoading) {
            return;
        }

        const previous = proposalResult;
        setProposalsError(null);
        setUpdatingProposalVoteIds(new Set(
            proposalResult?.proposedChangeEvents.map(item => item.analysisItemId) ?? [change.analysisItemId],
        ));
        setProposalResult(current => current ? {
            ...current,
            proposedChangeEvents: current.proposedChangeEvents.map(item =>
                item.analysisItemId === change.analysisItemId ? { ...item, vote } : item),
        } : current);

        try {
            const result = await updateAgentTaskProposedChangeVote(
                serviceName,
                change.analysisItemId,
                vote,
                change.rowVersion,
            );
            applyProposalUpdate(result);
        } catch (err) {
            setProposalResult(previous);
            setProposalsError(err instanceof Error ? err.message : 'Failed to update the proposed-change vote.');
        } finally {
            setUpdatingProposalVoteIds(new Set());
        }
    }

    async function handleOkAll() {
        const unvotedProposals = getCurrentActiveProposals(
            proposalResult?.proposedChangeEvents ?? [],
        ).filter(change => change.vote === null);
        if (okAllLoading || updatingProposalVoteIds.size > 0 || unvotedProposals.length === 0) {
            return;
        }

        setOkAllLoading(true);
        setProposalsError(null);
        setUpdatingProposalVoteIds(new Set(
            unvotedProposals.map(change => change.analysisItemId),
        ));
        try {
            let currentProposals = proposalResult?.proposedChangeEvents ?? [];
            for (const proposal of unvotedProposals) {
                const currentProposal = currentProposals.find(
                    change => change.analysisItemId === proposal.analysisItemId,
                ) ?? proposal;
                const result = await updateAgentTaskProposedChangeVote(
                    serviceName,
                    currentProposal.analysisItemId,
                    getOkAllProposalVote(
                        currentProposal,
                        proposalResult?.taskSnapshotInclusivePercent,
                    ),
                    currentProposal.rowVersion,
                );
                applyProposalUpdate(result);
                currentProposals = result.proposals;
            }
        } catch (err) {
            setProposalsError(err instanceof Error ? err.message : 'Failed to vote all unvoted proposed changes.');
        } finally {
            setUpdatingProposalVoteIds(new Set());
            setOkAllLoading(false);
        }
    }

    function handleInsertFreeFormSubstitution() {
        if (!freeFormSelectedToken) {
            return;
        }

        const selectionStart = freeFormEditorRef.current?.selectionStart ?? freeFormPromptText.length;
        const selectionEnd = freeFormEditorRef.current?.selectionEnd ?? freeFormPromptText.length;
        const next = insertSubstitutionReference(
            freeFormPromptText,
            selectionStart,
            selectionEnd,
            freeFormSelectedToken);
        updateFreeFormText(next.text);
        window.requestAnimationFrame(() => {
            freeFormEditorRef.current?.focus();
            freeFormEditorRef.current?.setSelectionRange(next.selectionStart, next.selectionEnd);
        });
    }

    function updateFreeFormText(nextText: string) {
        const nextRuntimeSubstitutions = updatePromptRanges(
            freeFormPromptText,
            nextText,
            freeFormRuntimeSubstitutions);
        const nextProtectedPlaceholders = updatePromptRanges(
            freeFormPromptText,
            nextText,
            freeFormProtectedPlaceholders);
        const allowedTokens = new Set(
            freeFormAllowedTokens.map(token => token.toLowerCase()));

        for (const match of nextText.matchAll(PROMPT_PLACEHOLDER_REGEX)) {
            const token = (match[1] ?? '').trim().toLowerCase();
            const candidate = {
                start: match.index,
                length: match[0].length,
            };
            if (!allowedTokens.has(token)
                || nextRuntimeSubstitutions.some(range => rangesOverlap(range, candidate))
                || nextProtectedPlaceholders.some(range => rangesOverlap(range, candidate))) {
                continue;
            }

            nextRuntimeSubstitutions.push(candidate);
        }

        setFreeFormPromptText(nextText);
        setFreeFormRuntimeSubstitutions(
            nextRuntimeSubstitutions.sort((left, right) => left.start - right.start));
        setFreeFormProtectedPlaceholders(nextProtectedPlaceholders);
    }

    const isRegressionTask = task?.taskType === 4;
    const hasQuestions = task?.workItems.some(item =>
        (item.openQuestionCount ?? 0)
        + (item.answeredQuestionCount ?? 0)
        + (item.declinedQuestionCount ?? 0) > 0) ?? false;
    const proposalHasData = !!proposalResult && (
        proposalResult.proposedChangeEvents.length > 0
        || !!proposalResult.resultSummary?.trim()
        || proposalResult.assumptions.length > 0
        || !!proposalResult.noChangeReason?.trim()
    );
    const relevantTabs = React.useMemo(() => {
        const tabs = new Set<TaskDetailTabKey>();
        if ((task?.pullRequests.length ?? 0) > 0) tabs.add('pull-requests');
        if (proposalHasData || !!proposalsError) tabs.add('proposed-changes');
        if ((task?.workItems.length ?? 0) > 0) tabs.add('job-executions');
        if (hasQuestions) tabs.add('clarifications');
        if (task?.snapshot && !isRegressionTask) tabs.add('profiles');
        if ((!isRegressionTask && !!task) || !!task?.regressionEvidence?.trim()) tabs.add('additional-context');
        return tabs;
    }, [hasQuestions, isRegressionTask, proposalHasData, proposalsError, task]);
    const requestedTab = searchParams.get('taskTab') as TaskDetailTabKey | null;
    const selectedTab = requestedTab && relevantTabs.has(requestedTab)
        ? requestedTab
        : getFirstRelevantTaskTab(relevantTabs);

    React.useEffect(() => {
        if (!task || proposalsLoading || !selectedTab || requestedTab === selectedTab) {
            return;
        }
        const next = new URLSearchParams(searchParams);
        next.set('taskTab', selectedTab);
        setSearchParams(next, { replace: true });
    }, [proposalsLoading, requestedTab, searchParams, selectedTab, setSearchParams, task]);

    if (loading) {
        return <Spinner size={SpinnerSize.large} label="Loading task details..." />;
    }

    if (error) {
        return <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>;
    }

    if (!task) {
        return (
            <MessageBar messageBarType={MessageBarType.warning}>
                The requested task could not be loaded.
            </MessageBar>
        );
    }

    const metricId = task.snapshot?.metricId ?? 0;
    const impact = calculateTaskImpact(proposalResult, task.workItems);
    const latestExecution = selectLatestWorkItem(task.workItems);
    const currentActiveProposals = (proposalResult?.proposedChangeEvents ?? [])
        .filter(proposal => proposal.isCurrent && proposal.outcome !== 'discarded');
    const taskStatus = getTaskLifecycleStatus({
        workItems: task.workItems,
        pullRequests: task.pullRequests,
        activeProposalCount: proposalResult ? currentActiveProposals.length : undefined,
        votedProposalCount: proposalResult
            ? currentActiveProposals.filter(proposal => proposal.vote !== null).length
            : undefined,
        okProposalCount: proposalResult
            ? currentActiveProposals.filter(proposal => proposal.vote === 0).length
            : undefined,
        hasStructuredAnalysis: proposalResult !== undefined,
    });
    const tabUrlValue = selectedTab ?? getFirstRelevantTaskTab(relevantTabs);
    const taskPageSearch = new URLSearchParams({ taskListId: String(taskListId) });
    if (snapshotId !== undefined) taskPageSearch.set('snapshotId', String(snapshotId));
    if (tabUrlValue) taskPageSearch.set('taskTab', tabUrlValue);
    const taskPageUrl = `/service/${serviceName}/agenttasks/task/${task.taskId}?${taskPageSearch.toString()}`;
    const impactValue = (value?: number | null) =>
        value === undefined || value === null ? 'Not estimated' : `${formatNumber(value)}%`;
    const setSelectedTab = (tab: TaskDetailTabKey) => {
        const next = new URLSearchParams(searchParams);
        next.set('taskTab', tab);
        setSearchParams(next);
    };

    return (
        <Stack tokens={{ childrenGap: 16 }}>
            {task.isSynthetic && <SyntheticBanner visible={true} />}

            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    padding: '4px 0 12px',
                    borderBottom: '1px solid #edebe9',
                    background: '#ffffff',
                }}
            >
                <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <Text as="h1" variant="xLarge" styles={{ root: { margin: 0 } }}>{task.scope}</Text>
                    <span
                        data-testid="task-lifecycle-status"
                        style={{
                            border: '1px solid #c8c6c4',
                            borderRadius: 12,
                            padding: '2px 9px',
                            fontSize: 12,
                            fontWeight: 600,
                            background: taskStatus === 'done' || taskStatus === 'readyToCode'
                                ? '#dff6dd'
                                : taskStatus === 'failed' || taskStatus === 'prBlocked'
                                    ? '#fde7e9'
                                    : taskStatus === 'needsInput'
                                        ? '#fff4ce'
                                        : taskStatus === 'notStarted' || taskStatus === 'analyzedNoFix' || taskStatus === 'other'
                                            ? '#f3f2f1'
                                            : '#deecf9',
                        }}
                    >
                        {getTaskLifecycleStatusLabel(taskStatus)}
                    </span>
                </Stack>
                <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 10, rowGap: 8 } }}>
                    {standalone ? (
                        <>
                            <DefaultButton
                                text="Back to task list"
                                onClick={() => navigate(`/service/${serviceName}/agenttasks/list/${taskListId}${snapshotId !== undefined ? `?snapshotId=${snapshotId}` : ''}`)}
                            />
                            <DefaultButton
                                text="Task Dashboard"
                                onClick={() => navigate(`/service/${serviceName}/agenttasks/dashboard`)}
                            />
                        </>
                    ) : (
                        <DefaultButton text="Open full task page" onClick={() => navigate(taskPageUrl)} />
                    )}
                    <Dropdown
                        ariaLabel="Job"
                        options={availableJobs.map<IDropdownOption>(job => ({
                            key: job.name,
                            text: job.name,
                        }))}
                        selectedKey={selectedJobName}
                        onChange={(_, option) => {
                            setSelectedJobName(option?.key ? String(option.key) : undefined);
                            setEnqueueError(null);
                            setEnqueueMessage(null);
                            resetFreeFormEditor();
                        }}
                        placeholder={jobsLoading
                            ? 'Loading job definitions...'
                            : (availableJobs.length > 0 ? 'Select a job' : 'No enabled jobs')}
                        disabled={jobsLoading
                            || enqueueLoading
                            || freeFormPreviewLoading
                            || freeFormSubmitting
                            || availableJobs.length === 0}
                        styles={{ root: { minWidth: 260 } }}
                    />
                    <DefaultButton
                        text={enqueueLoading ? 'Enqueueing...' : 'Enqueue'}
                        onClick={() => void handleEnqueue()}
                        disabled={jobsLoading
                            || enqueueLoading
                            || freeFormPreviewLoading
                            || freeFormSubmitting
                            || !selectedJobName
                            || availableJobs.length === 0
                            || !task.snapshot}
                    />
                    <DefaultButton
                        text={freeFormPreviewLoading ? 'Preparing...' : 'Free-form enqueue'}
                        onClick={() => void handleOpenFreeFormEnqueue()}
                        disabled={jobsLoading
                            || enqueueLoading
                            || freeFormPreviewLoading
                            || freeFormSubmitting
                            || !selectedJobName
                            || availableJobs.length === 0
                            || !task.snapshot}
                    />
                    {!standalone && (onNavigatePreviousTask || onNavigateNextTask) && (
                        <Stack
                            horizontal
                            tokens={{ childrenGap: 8 }}
                            style={{ marginLeft: 64 }}
                            data-testid="task-navigation-controls"
                        >
                            {onNavigatePreviousTask && (
                                <DefaultButton
                                    text="Previous task"
                                    disabled={!hasPreviousTask}
                                    onClick={onNavigatePreviousTask}
                                />
                            )}
                            {onNavigateNextTask && (
                                <DefaultButton
                                    text="Next task"
                                    disabled={!hasNextTask}
                                    onClick={onNavigateNextTask}
                                />
                            )}
                        </Stack>
                    )}
                </Stack>
            </div>

            <header>
                <Stack horizontal wrap tokens={{ childrenGap: 24 }} styles={{ root: { rowGap: 6 } }}>
                    <Text><strong>Task share of snapshot:</strong> {impactValue(impact.taskShare)}</Text>
                    <Text>
                        <strong>Proposed task reduction:</strong> {impactValue(impact.allTaskReduction)}
                        {impact.hasVotes && <> (Ok votes only: {impactValue(impact.okTaskReduction)})</>}
                    </Text>
                    <Text>
                        <strong>Proposed snapshot reduction:</strong> {impactValue(impact.allSnapshotReduction)}
                        {impact.hasVotes && <> (Ok votes only: {impactValue(impact.okSnapshotReduction)})</>}
                    </Text>
                </Stack>

                {latestExecution && (
                    <Text block styles={{ root: { marginTop: 8, color: '#605e5c' } }}>
                        <strong>Latest execution:</strong> {latestExecution.jobName}
                        {' • '}{formatExecutionDate(latestExecution.agentStartDate)}
                        {' • '}{getJobStatusLabel(latestExecution.jobStatus)}
                        {' • '}
                        <TaskExecutionLinks
                            serviceName={serviceName}
                            service={service}
                            taskListId={taskListId}
                            taskId={taskId}
                            item={latestExecution}
                            showLabel={false}
                        />
                    </Text>
                )}
                {enqueueError && (
                    <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginTop: 8 } }}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{enqueueError}</div>
                    </MessageBar>
                )}
                {enqueueMessage && (
                    <MessageBar messageBarType={MessageBarType.success} styles={{ root: { marginTop: 8 } }}>
                        {enqueueMessage}
                    </MessageBar>
                )}
            </header>

            <Dialog
                hidden={!freeFormDialogOpen}
                onDismiss={resetFreeFormEditor}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: 'Free-form enqueue',
                    subText: selectedJobName
                        ? `Edit the task-resolved prompt body for ${selectedJobName}. Automatic backend instructions are added separately.`
                        : undefined,
                }}
                modalProps={{ isBlocking: freeFormSubmitting }}
                minWidth={720}
                maxWidth={960}
            >
                <Stack tokens={{ childrenGap: 12 }}>
                    {freeFormError && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{freeFormError}</div>
                        </MessageBar>
                    )}
                    {freeFormContainsSasPlaceholders && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            SAS placeholders stay in the saved prompt and are replaced with fresh URLs when the job is dispatched.
                        </MessageBar>
                    )}
                    <Text><strong>Task:</strong> {task.scope}</Text>
                    <Text><strong>Snapshot:</strong> {task.snapshot?.snapshotId}</Text>
                    {freeFormAllowedTokens.length > 0 && (
                        <Stack horizontal verticalAlign="end" tokens={{ childrenGap: 8 }}>
                            <Dropdown
                                label="Insert runtime substitution"
                                options={JOB_RUNTIME_SUBSTITUTION_OPTIONS
                                    .filter(option => freeFormAllowedTokens.includes(option.token))
                                    .map<IDropdownOption>(option => ({
                                        key: option.token,
                                        text: option.token,
                                        data: option.description,
                                    }))}
                                selectedKey={freeFormSelectedToken}
                                onChange={(_, option) => setFreeFormSelectedToken(
                                    option?.key ? String(option.key) : undefined)}
                                disabled={freeFormSubmitting}
                                styles={{ root: { minWidth: 420 } }}
                            />
                            <DefaultButton
                                text="Insert"
                                onClick={handleInsertFreeFormSubstitution}
                                disabled={freeFormSubmitting || !freeFormSelectedToken}
                            />
                        </Stack>
                    )}
                    <div>
                        <Label htmlFor="agent-task-free-form-prompt">Prompt body</Label>
                        <textarea
                            id="agent-task-free-form-prompt"
                            ref={freeFormEditorRef}
                            value={freeFormPromptText}
                            onChange={event => updateFreeFormText(event.target.value)}
                            disabled={freeFormSubmitting}
                            style={{
                                width: '100%',
                                minHeight: 360,
                                padding: 12,
                                borderRadius: 6,
                                border: '1px solid #c8c6c4',
                                fontFamily: 'Consolas, "Courier New", monospace',
                                fontSize: 13,
                                lineHeight: 1.5,
                                resize: 'vertical',
                            }}
                        />
                    </div>
                </Stack>
                <DialogFooter>
                    <PrimaryButton
                        text={freeFormSubmitting ? 'Enqueueing...' : 'Submit free-form'}
                        onClick={() => void handleSubmitFreeFormEnqueue()}
                        disabled={freeFormSubmitting}
                    />
                    <DefaultButton text="Cancel" onClick={resetFreeFormEditor} disabled={freeFormSubmitting} />
                </DialogFooter>
            </Dialog>

            {relevantTabs.size === 0 ? (
                <MessageBar messageBarType={MessageBarType.info}>No task detail sections are available.</MessageBar>
            ) : (
                <>
                    <Pivot
                        selectedKey={selectedTab}
                        onLinkClick={item => {
                            const key = item?.props.itemKey as TaskDetailTabKey | undefined;
                            if (key) setSelectedTab(key);
                        }}
                    >
                        {relevantTabs.has('pull-requests') && <PivotItem headerText="Pull requests" itemKey="pull-requests" />}
                        {relevantTabs.has('proposed-changes') && <PivotItem headerText="Proposed changes" itemKey="proposed-changes" />}
                        {relevantTabs.has('job-executions') && <PivotItem headerText="Job executions" itemKey="job-executions" />}
                        {relevantTabs.has('clarifications') && <PivotItem headerText="Clarifications" itemKey="clarifications" />}
                        {relevantTabs.has('profiles') && <PivotItem headerText="Profiles" itemKey="profiles" />}
                        {relevantTabs.has('additional-context') && <PivotItem headerText="Additional context" itemKey="additional-context" />}
                    </Pivot>

                    <div style={{ minHeight: 240 }}>
                        {selectedTab === 'pull-requests' && (
                            <PullRequestSummaryList
                                service={service}
                                pullRequests={task.pullRequests}
                                workItems={task.workItems}
                                getDetailUrl={pullRequest => {
                                    const query = new URLSearchParams({
                                        taskId: String(task.taskId),
                                        prId: String(pullRequest.prId),
                                    });
                                    if (snapshotId !== undefined) query.set('snapshotId', String(snapshotId));
                                    return `/service/${serviceName}/agenttasks/list/${taskListId}?${query.toString()}`;
                                }}
                            />
                        )}

                        {selectedTab === 'proposed-changes' && (
                            <Stack tokens={{ childrenGap: 12 }}>
                                {proposalsError && (
                                    <MessageBar
                                        messageBarType={MessageBarType.error}
                                        actions={(
                                            <DefaultButton
                                                text="Retry"
                                                disabled={proposalsLoading}
                                                onClick={() => void loadProposals()}
                                            />
                                        )}
                                    >
                                        {proposalsError}
                                    </MessageBar>
                                )}
                                {proposalsLoading ? (
                                    <Spinner size={SpinnerSize.medium} label="Loading proposed changes..." />
                                ) : proposalResult ? (
                                    <TaskScopedProposalReview
                                        resultSet={proposalResult}
                                        onVoteChange={handleProposalVote}
                                        onSelectionChange={handleProposalSelection}
                                        updatingProposalIds={updatingProposalVoteIds}
                                        selectedProposalIds={selectedProposalIds}
                                        onOkAll={() => void handleOkAll()}
                                        okAllLoading={okAllLoading}
                                    />
                                ) : (
                                    <MessageBar messageBarType={MessageBarType.info}>No proposed changes were recorded.</MessageBar>
                                )}
                            </Stack>
                        )}

                        {selectedTab === 'job-executions' && (
                            <TaskJobExecutions
                                serviceName={serviceName}
                                service={service}
                                taskListId={taskListId}
                                taskId={taskId}
                                workItems={task.workItems}
                                onWorkItemUpdated={updated => setTask(current => current ? {
                                    ...current,
                                    workItems: current.workItems.map(item => item.workItemId === updated.workItemId ? {
                                        ...item,
                                        ...updated,
                                    } : item),
                                } : current)}
                            />
                        )}

                        {selectedTab === 'clarifications' && (
                            <TaskClarificationsTab
                                serviceName={serviceName}
                                taskListId={taskListId}
                                taskId={taskId}
                            />
                        )}

                        {selectedTab === 'profiles' && (
                            <Stack tokens={{ childrenGap: 16 }}>
                                <section aria-label="Profile tools">
                                    <Text block styles={{ root: { color: '#605e5c' } }}>
                                        Snapshot #{task.snapshot?.snapshotId} • Metric {task.snapshot?.metricId ?? '-'}
                                    </Text>
                                    {!task.isSynthetic && (
                                        <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 12, rowGap: 12 } }}>
                                            {(['inclusive', 'exclusive'] as TaskProfileStreamKind[]).map(kind => (
                                                <button
                                                    key={kind}
                                                    type="button"
                                                    onClick={() => void handleOpenProfileStream(kind)}
                                                    disabled={profileStreamLoadingKind !== null}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '6px 10px',
                                                        borderRadius: 6,
                                                        border: '1px solid #d1d1d1',
                                                        background: '#ffffff',
                                                    }}
                                                >
                                                    {profileStreamLoadingKind === kind
                                                        ? <Spinner size={SpinnerSize.small} />
                                                        : <img src={ProfilerIcon} alt="" style={{ width: 14, height: 14 }} />}
                                                    <span>{TASK_PROFILE_STREAM_KEYS[kind].label} profile</span>
                                                </button>
                                            ))}
                                            {(['flat', 'nested'] as CallGraphDownloadFormat[]).map(format => (
                                                <button
                                                    key={format}
                                                    type="button"
                                                    onClick={() => void handleDownloadCallGraph(format)}
                                                    disabled={callGraphDownloadLoadingFormat !== null}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '6px 10px',
                                                        borderRadius: 6,
                                                        border: '1px solid #d1d1d1',
                                                        background: '#ffffff',
                                                    }}
                                                >
                                                    {callGraphDownloadLoadingFormat === format && <Spinner size={SpinnerSize.small} />}
                                                    <span>{TASK_CALL_GRAPH_DOWNLOAD_LABELS[format]}</span>
                                                </button>
                                            ))}
                                        </Stack>
                                    )}
                                    {profileStreamError && <MessageBar messageBarType={MessageBarType.error}>{profileStreamError}</MessageBar>}
                                    {callGraphDownloadError && <MessageBar messageBarType={MessageBarType.error}>{callGraphDownloadError}</MessageBar>}
                                </section>
                                <AgentTaskFlameGraph
                                    serviceName={serviceName}
                                    taskListId={taskListId}
                                    taskId={task.taskId}
                                    snapshotId={snapshotId}
                                    metricId={metricId}
                                />
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <StatCard title="Inclusive" value={formatNumber(task.snapshotData.inclusive)} />
                                    <StatCard title="Exclusive" value={formatNumber(task.snapshotData.exclusive)} />
                                    <StatCard title="Methods" value={String(task.snapshotData.methodCount)} />
                                    <StatCard title="Subtasks" value={String(task.snapshotData.subTaskCount)} />
                                    <StatCard title="Depth" value={String(task.snapshotData.depth)} />
                                </Stack>
                            </Stack>
                        )}

                        {selectedTab === 'additional-context' && (
                            <Stack tokens={{ childrenGap: 12 }}>
                                <Text>
                                    Review performance scope <strong>{task.scope}</strong>
                                    {task.snapshot ? ` from snapshot #${task.snapshot.snapshotId}.` : '.'}
                                </Text>
                                {task.snapshot && (
                                    <Text styles={{ root: { color: '#605e5c' } }}>
                                        Source snapshot: {task.snapshot.name || formatCompactDate(task.snapshot.date)}
                                        {' • '}Date {formatCompactDate(task.snapshot.date)}
                                        {' • '}Environment {task.snapshot.environmentId}
                                    </Text>
                                )}
                                {isRegressionTask ? (
                                    <>
                                        <MessageBar messageBarType={MessageBarType.info}>
                                            Regression evidence is captured immutably for this commit and cannot be edited.
                                        </MessageBar>
                                        <TextField
                                            label="Captured regression evidence"
                                            multiline
                                            autoAdjustHeight
                                            readOnly
                                            value={task.regressionEvidence ?? 'Evidence has not been captured yet.'}
                                        />
                                    </>
                                ) : (
                                    <>
                                        {extraContextError && <MessageBar messageBarType={MessageBarType.error}>{extraContextError}</MessageBar>}
                                        <div style={{ border: '1px solid #e1dfdd', borderRadius: 8, padding: 12 }}>
                                            <TextField
                                                label="Add extra context"
                                                multiline
                                                rows={3}
                                                value={newExtraContextInfo}
                                                onChange={(_, value) => setNewExtraContextInfo(value ?? '')}
                                                disabled={savingContextId !== null || editingContextId !== null}
                                            />
                                            <PrimaryButton
                                                text={savingContextId === 'create' ? 'Adding...' : 'Add context'}
                                                onClick={() => void handleCreateExtraContext()}
                                                disabled={savingContextId !== null || editingContextId !== null}
                                                styles={{ root: { marginTop: 12 } }}
                                            />
                                        </div>
                                        {task.extraContext.length === 0 ? (
                                            <MessageBar messageBarType={MessageBarType.info}>
                                                No extra context has been attached to this task yet.
                                            </MessageBar>
                                        ) : task.extraContext.map(item => (
                                            <div key={item.contextId} style={{ border: '1px solid #e1dfdd', borderRadius: 8, padding: 12 }}>
                                                {editingContextId === item.contextId ? (
                                                    <Stack tokens={{ childrenGap: 12 }}>
                                                        <TextField
                                                            label={`Context #${item.contextId}`}
                                                            multiline
                                                            rows={3}
                                                            value={editingContextInfo}
                                                            onChange={(_, value) => setEditingContextInfo(value ?? '')}
                                                            disabled={savingContextId !== null}
                                                        />
                                                        <Checkbox
                                                            label="Disabled"
                                                            checked={editingContextDisabled}
                                                            onChange={(_, checked) => setEditingContextDisabled(checked ?? false)}
                                                            disabled={savingContextId !== null}
                                                        />
                                                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                                                            <PrimaryButton
                                                                text={savingContextId === item.contextId ? 'Saving...' : 'Save'}
                                                                onClick={() => void handleSaveEditedExtraContext()}
                                                                disabled={savingContextId !== null}
                                                            />
                                                            <DefaultButton text="Cancel" onClick={cancelEditingExtraContext} disabled={savingContextId !== null} />
                                                        </Stack>
                                                    </Stack>
                                                ) : (
                                                    <Stack tokens={{ childrenGap: 8 }}>
                                                        <Text>{item.info}</Text>
                                                        <Text styles={{ root: { color: item.disabled ? '#a4262c' : '#605e5c' } }}>
                                                            Context #{item.contextId} • {item.disabled ? 'Disabled' : 'Active'}
                                                        </Text>
                                                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                                                            <DefaultButton
                                                                text="Edit"
                                                                onClick={() => beginEditingExtraContext(item)}
                                                                disabled={savingContextId !== null || editingContextId !== null}
                                                            />
                                                            <DefaultButton
                                                                text={item.disabled ? 'Enable' : 'Disable'}
                                                                onClick={() => void handleToggleExtraContextDisabled(item)}
                                                                disabled={savingContextId !== null || editingContextId !== null}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                )}
                                            </div>
                                        ))}
                                    </>
                                )}
                                <Text styles={{ root: { color: '#605e5c' } }}>
                                    Task list: {effectiveTaskListDisplayName} • Created {formatExecutionDate(task.createdDate)}
                                </Text>
                            </Stack>
                        )}
                    </div>
                </>
            )}
        </Stack>
    );
}
